import { Component, OnDestroy, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { paymentNavigationItems, PaymentService } from '../../../core/services/payment.service';
import { TranslateService } from '@ngx-translate/core';
import { ChargeService } from '../../../core/services/charge.service';
import { paymentSteps, StepService } from '../../../core/services/step.service';
import { Subscription } from 'rxjs';
import { ErrorService } from '../../../core/services/error.service';
import { PaymentMethodModel } from '../../../core/models/payment-method.model';
import { UtilsService } from '../../../core/services/utils.service';
import { HelperService } from '../../../core/services/helper.service';
import { DomSanitizer } from '@angular/platform-browser';
import { paymentStatuses } from '../../../core/services/payment.service';
import { PaymentOptionModel } from '../../../core/models/payment-option.model';
import { ChargeModel } from '../../../core/models/charge.model';
import { PaymentModel } from '../../../core/models/payment.model';
import { ClaimRefundModel } from '../../../core/models/claim-refund.model';
import { TransactionStatusModel } from '../../../core/models/transaction-status.models';
import { PaymentNetworkModel } from '../../../core/models/payment-network.model';
import { StepModel } from '../../../core/models/step.model';

@Component({
  selector: 'app-payment-processor',
  templateUrl: './payment-processor.component.html',
  styleUrls: ['./payment-processor.component.scss']
})
export class PaymentProcessorComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];

  charge: ChargeModel;
  payment: PaymentModel = new PaymentModel();
  selectedPaymentMethod: PaymentMethodModel;
  selectedPaymentOption = new PaymentOptionModel();
  selectedPaymentNetwork = new PaymentNetworkModel();

  isPaymentLoaderShown = true;
  isPaymentNavShown = false;
  isPaymentCurrencyAlertShown = false;
  isPaymentCurrencyInfoShown = false;
  isPaymentDetailsInfoShown = false;
  isContentTerminatePaymentShown = false;

  isPaymentOverpaid = false;
  isPaymentUnderpaid = false;
  returnModal = true

  qrData = '';
  amount: number;
  cryptoAmount = '';

  QRCodeIcon = '';
  callbackEndpoint = '';

  paymentStatuses = paymentStatuses;
  paymentStatus: string = paymentStatuses.IN_PROGRESS;
  paymentStatusIcon = '';
  paymentProgressPercentage = 100;
  paymentProgressTime = '';

  paymentNavigationItems = paymentNavigationItems;
  selectedNavigationItem: string = paymentNavigationItems.SCAN;
  deepLinks = {};
  selectedDeepLink = '';
  paymentDescriptionType = '';

  isRedirectShown = false;
  isMobile = false;

  // overpaid
  overpaidStatusIcon = '';
  amountRefund = 0;

  // underpaid
  underpaidStatusIcon = '';

  constructor(
    private translate: TranslateService,
    private chargeService: ChargeService,
    public paymentService: PaymentService,
    private utilsService: UtilsService,
    private stepService: StepService,
    private errorService: ErrorService,
    private clipboard: Clipboard,
    public helperService: HelperService,
    public sanitizing: DomSanitizer
  ) {
    // set charge details
    this.charge = this.chargeService.charge$$.value;

    // init data
    this.overpaidStatusIcon = this.helperService.paymentStatusesIcons[paymentStatuses.OVERPAID];
    this.underpaidStatusIcon = this.helperService.paymentStatusesIcons[paymentStatuses.UNDERPAID];
    this.amount = this.charge.amount / 100;

    // set payment method
    this.selectedPaymentMethod = this.paymentService.selectedPaymentMethod$$.value;
  }

  ngOnInit() {
    // subscribe to payment
    this.subscriptions.push(
      this.paymentService.payment$$.subscribe((payment: PaymentModel) => {
        // set payment data
        this.payment = payment;
      })
    );

    // subscribe to selected payment option
    this.subscriptions.push(
      this.paymentService.selectedPaymentOption$$.subscribe((selectedPaymentOption: PaymentOptionModel) => {
        // set selected payment option
        this.selectedPaymentOption = selectedPaymentOption;
      })
    );

    // subscribe to selected payment network
    this.subscriptions.push(
      this.paymentService.selectedPaymentNetwork$$.subscribe((selectedPaymentNetwork: PaymentNetworkModel) => {
        // set selected payment network
        this.selectedPaymentNetwork = selectedPaymentNetwork;
      })
    );

    // subscribe to deep links
    this.subscriptions.push(
      this.paymentService.deepLinks$$.subscribe((deepLinks) => {
        this.deepLinks = deepLinks;
        if (this.selectedPaymentOption && this.selectedPaymentOption.code in deepLinks) {
          this.selectedDeepLink = deepLinks[this.selectedPaymentOption.code];
        } else if (this.selectedPaymentMethod.type in deepLinks) {
          this.selectedDeepLink = deepLinks[this.selectedPaymentMethod.type];
        }
      })
    );

    // subscribe to payment progress percentage
    this.subscriptions.push(
      this.paymentService.paymentProgressPercentage$$.subscribe((paymentProgressPercentage) => {
        this.paymentProgressPercentage = paymentProgressPercentage;
      })
    );

    // subscribe to payment progress time
    this.subscriptions.push(
      this.paymentService.paymentProgressTime$$.subscribe((paymentProgressTime) => {
        this.paymentProgressTime = paymentProgressTime;
      })
    );

    // subscribe to payment status
    this.subscriptions.push(
      this.paymentService.paymentStatus$$.subscribe({
        next: (status) => {
          // set payment status
          this.paymentStatus = status;

          // set payment status icon
          this.paymentStatusIcon = this.helperService.paymentStatusesIcons[this.paymentStatus];

          // show correct payment status
          if (status === paymentStatuses.IN_PROGRESS) {
            this.stepService.currentStep$$.next({ ...this.stepService.currentStep$$.value, isLocked: true });
            return;
          }

          if (status === paymentStatuses.SUCCESS) {
            this.setPaymentSuccess();
            return;
          }

          if (status === paymentStatuses.EXPIRED) {
            this.setPaymentTimeout();
            return;
          }

          if (status === paymentStatuses.OVERPAID) {
            this.setPaymentOverpaid();
            return;
          }

          if (status === paymentStatuses.UNDERPAID) {
            this.isPaymentUnderpaid = false;
            this.stepService.currentStep$$.next({ ...this.stepService.currentStep$$.value, isLocked: true });
            return;
          }

          if (status === paymentStatuses.TERMINATED) {
            this.onTerminatePayment();
            return;
          }

          this.setPaymentFailed();
        },
        error: () => {
          this.setPaymentFailed();
        }
      })
    );

    // subscribe to transaction status
    this.subscriptions.push(
      this.paymentService.transactionStatus$$.subscribe((transactionStatus: TransactionStatusModel) => {
        // payment refund amount
        const diffAmount = parseFloat(transactionStatus.diff_amount ?? '0');
        this.amountRefund = Math.abs(diffAmount);
        // set claim refund form data
        const claimRefundForm = new ClaimRefundModel();
        claimRefundForm.paymentMethod = this.selectedPaymentMethod;
        claimRefundForm.paymentOption = this.selectedPaymentOption;
        claimRefundForm.transactionId = this.payment.transaction_id;
        claimRefundForm.amountRefund = this.amountRefund;
        claimRefundForm.amountPaid =
          ((this.paymentService.paymentInProgress$$.value?.amount
            ? this.paymentService.paymentInProgress$$.value?.amount
            : this.charge.amount) +
            diffAmount * 100) /
          100;
        this.paymentService.claimRefundForm$$.next(claimRefundForm);
        // check if payment is underpaid
        if (this.qrData !== transactionStatus.qr_data && diffAmount < 0) {
          this.stepService.currentStep$$.next({ ...this.stepService.currentStep$$.value, isLocked: true });
          const diffCryptoAmount = parseFloat(transactionStatus.diff_crypto_amount ?? '0');
          this.isPaymentUnderpaid = true;
          this.qrData = transactionStatus.qr_data ?? this.payment.qr_data;
          this.amount = this.amountRefund ?? this.amount;
          this.cryptoAmount = Math.abs(diffCryptoAmount).toString() ?? this.payment.crypto_amount;
        }
      })
    );

    // get content terminate payment info
    this.subscriptions.push(
      this.utilsService.isContentTerminatePaymentShown$$.subscribe((isContentTerminatePaymentShown) => {
        this.isContentTerminatePaymentShown = isContentTerminatePaymentShown;
      })
    );

    // subscribe to is mobile
    this.subscriptions.push(
      this.utilsService.isMobile$$.subscribe((isMobile) => {
        this.isMobile = isMobile;
      })
    );

    // generate payment
    this.generatePayment();
  }

  ngOnDestroy() {
    // reset payment
    this.paymentService.resetPayment(true);
    // unsubscribe
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  generatePayment() {
    /** Omit payment generation if the payment is already in progress */
    if (this.paymentService.paymentInProgress$$.value) {
      this.payment = this.paymentService.payment$$.value;
      this.qrData = this.payment.qr_data;
      this.cryptoAmount = this.payment.crypto_amount;
      this.QRCodeIcon = this.paymentService.getQRCodeIcon();

      if (this.paymentService.paymentInProgress$$.value?.amount) {
        this.amount = this.paymentService.paymentInProgress$$.value?.amount / 100;
      }

      if (this.paymentService.paymentInProgress$$.value.diff_amount) {
        const diffAmount = parseFloat(this.paymentService.paymentInProgress$$.value.diff_amount ?? '0');
        this.amountRefund = Math.abs(diffAmount);
      }

      if (this.selectedPaymentOption.section === 'CRYPTO_CURRENCY') {
        // show payment currency alert
        this.isPaymentCurrencyAlertShown = true;
        // show payment currency info
        this.isPaymentCurrencyInfoShown = true;
      }

      if (this.selectedPaymentOption.section === 'CRYPTO_CURRENCY' && this.charge.created_at) {
        // show payment navigation bar
        this.isPaymentNavShown = true;
      }

      this.setInitialPaymentProgress(this.payment);

      this.paymentService.startTransactionStatusInterval(true, this.payment.transaction_id);
      // set payment description
      this.setPaymentDescription();
      this.isPaymentLoaderShown = false;

      /** In case the user decides to go back and choose another payment option, reset the payment in progress as well as the payment. The next time the user comes to the payment processor it will baypasse the above condition and generate a new payment */
      const currentStep = this.stepService.currentStep$$.subscribe({
        next: (step: StepModel) => {
          if (step.step === paymentSteps.PAYMENT_PROCESSOR) {
            return;
          }

          /** Reset the payments */
          this.paymentService.payment$$.next(new PaymentModel());
          if (this.paymentStatus !== paymentStatuses.OVERPAID && this.paymentStatus !== paymentStatuses.UNDERPAID) {
            this.paymentService.paymentInProgress$$.next(null);
          }

          /** Unsubscribe on back step */
          currentStep.unsubscribe();
        },
        error: (error) => this.setContentError(error.error.code)
      });

      return;
    }

    /** Hide back button */
    this.stepService.updateLoading(true);

    /** Generate payment */
    this.subscriptions.push(
      this.paymentService
        .generatePayment(this.selectedPaymentMethod, this.selectedPaymentOption, this.selectedPaymentNetwork)
        .subscribe({
          next: (payment: PaymentModel) => {
            // make redirect if link exists
            if (payment.redirect_url) {
              this.utilsService.skipUnloadConfirm$$.next(true);
              window.location.href = payment.redirect_url;
            } else {
              // set payment data
              this.paymentService.payment$$.next(payment);
              // set qr data
              this.qrData = payment.qr_data;
              // set crypto amount
              this.cryptoAmount = payment.crypto_amount;
              // get QR code icon
              this.QRCodeIcon = this.paymentService.getQRCodeIcon();
              // check if selected payment option is CRYPTO_CURRENCY
              if (this.selectedPaymentOption.section === 'CRYPTO_CURRENCY') {
                // show payment navigation bar
                this.isPaymentNavShown = true;
                // show payment currency alert
                this.isPaymentCurrencyAlertShown = true;
                // show payment currency info
                this.isPaymentCurrencyInfoShown = true;
              }
              // set initial payment progress time
              this.setInitialPaymentProgress(payment);
              // start payment timer
              this.paymentService.startTransactionStatusInterval(true, payment.transaction_id);
              // set payment description
              this.setPaymentDescription();
              // hide loader
              this.isPaymentLoaderShown = false;
            }
          },
          error: (e) => this.setContentError(e.error.code),
          complete: () => {
            // hide loader
            this.isPaymentLoaderShown = false;
            /** Show back button */
            this.stepService.updateLoading(false);
          }
        })
    );
  }

  setSelectedNavigationItem(item: string) {
    this.selectedNavigationItem = item;
  }

  setPaymentDescription() {
    if (this.selectedPaymentOption.code && this.selectedPaymentNetwork) {
      this.paymentDescriptionType = `${this.selectedPaymentOption.code}-${this.selectedPaymentNetwork.network_type}`;
    } else if (this.selectedPaymentOption.code) {
      this.paymentDescriptionType = this.selectedPaymentOption.code;
    } else {
      this.paymentDescriptionType = this.selectedPaymentMethod.type;
    }
  }

  setInitialPaymentProgress(payment: PaymentModel) {
    // set payment full time
    this.paymentService.paymentFullTime$$.next(
      this.utilsService.timeDifference(payment.expires_at, payment.created_at)
    );
    // set payment time left
    this.paymentService.paymentTimeLeft$$.next(
      this.utilsService.timeDifference(payment.expires_at, this.utilsService.getUTCTimestamp())
    );
    // set payment progress time
    this.paymentService.paymentProgressTime$$.next(
      this.utilsService.parseTime(this.paymentService.paymentTimeLeft$$.value)
    );
  }

  setPaymentSuccess() {
    // set payment status
    this.paymentStatus = paymentStatuses.SUCCESS;
    // reset payment elements
    this.resetPaymentCommonElements();
    // remove selected payment option
    // this.paymentService.selectedPaymentOption$$.next(new PaymentOptionModel());
    // skip unload confirm
    this.utilsService.skipUnloadConfirm$$.next(true);
    // set timer to delete charge and redirect user to callback endpoint
    setTimeout(() => {
      this.callbackEndpoint = this.utilsService.modifyUrl(
        this.charge.callback_endpoint,
        `payment_method=${this.payment.payment_method}&payment_status=${this.paymentStatus}&transaction_id=${this.payment.transaction_id}`
      );
      this.chargeService.deleteCharge().subscribe({
        error: () => (window.location.href = this.callbackEndpoint),
        complete: () => (window.location.href = this.callbackEndpoint)
      });
    }, 2000);
    // set timer to show redirect button if user is not redirected
    setTimeout(() => {
      this.isRedirectShown = true;
    }, 4000);
  }

  setPaymentTimeout() {
    // set payment status
    if (this.paymentStatus !== paymentStatuses.UNDERPAID) {
      this.paymentStatus = paymentStatuses.EXPIRED;
    }
    // reset payment elements
    this.resetPaymentCommonElements();
    // show content terminate payment
    this.utilsService.showContentTerminatePayment();
  }

  setPaymentOverpaid() {
    // terminate transaction status interval
    this.paymentService.terminateTransactionStatusInterval();
    this.resetPaymentCommonElements();
    this.isPaymentUnderpaid = false;
    this.isPaymentOverpaid = true;
  }

  closeReturnModal() {
    this.returnModal = false
  }

  setPaymentFailed() {
    // set payment status
    this.paymentStatus = paymentStatuses.FAILED;
    // reset payment elements
    this.resetPaymentCommonElements();
    // show content terminate payment
    this.utilsService.showContentTerminatePayment();
  }

  setContentError(errorCode: string) {
    const errorDetails = this.errorService.getErrorDetails(errorCode);
    this.isPaymentLoaderShown = false;
    this.stepService.updateLoading(false);
    this.errorService.setAlert('info', this.translate.instant(`errors.${errorDetails['code']}`));
    this.stepService.toStep(errorDetails['to_step']);
  }

  onTerminatePayment() {
    this.paymentService.terminateTransactionStatusInterval();

    /** In case of is_persistent property being set to TRUE do not redirect the user back to the shop but instead position him back on the payment method selection inside the UI */
    if (this.chargeService.charge$$.value.is_persistent) {
      this.paymentService.terminatePayment().subscribe({
        error: (error) => {
          console.log('error on termination: ', error.message);
        }
      });
      this.utilsService.skipUnloadConfirm$$.next(true);
      this.stepService.toStep(paymentSteps.PAYMENT_METHOD_SELECT);
      this.paymentService.resetPayment();
      return;
    }

    this.utilsService.showBoxLoader();
    const paymentMethod = this.paymentService.selectedPaymentMethod$$.value.type;
    const callbackEndpoint = this.utilsService.modifyUrl(
      this.chargeService.charge$$.value.callback_endpoint,
      `payment_method=${paymentMethod}&payment_status=${paymentStatuses.TERMINATED}`
    );
    this.utilsService.skipUnloadConfirm$$.next(true);
    this.paymentService.terminatePayment().subscribe({
      error: () => (window.location.href = callbackEndpoint),
      complete: () => (window.location.href = callbackEndpoint)
    });
  }

  onRetry() {
    // reset payment
    this.paymentService.resetPayment();

    // show payment loader
    this.isPaymentLoaderShown = true;

    // Hide the back button
    this.stepService.updateLoading(true);

    // reset previous step button
    if (!this.chargeService.charge$$.value.is_persistent) {
      this.stepService.currentStep$$.next({ ...this.stepService.currentStep$$.value, isLocked: false });
    }

    // show terminate payment
    this.utilsService.showTerminatePayment();

    // hide content terminate payment
    this.utilsService.hideContentTerminatePayment();

    // generate payment
    this.generatePayment();
  }

  onOpenDeepLink() {
    this.utilsService.skipUnloadConfirm$$.next(true);
    window.location.href = this.selectedDeepLink;
  }

  onCopyText(text: string, type: string) {
    this.errorService.hideAlert();
    this.clipboard.copy(text);
    this.errorService.setAlert('info', this.translate.instant(`copy_success_${type}`));
  }

  onClaimRefundForm() {
    this.stepService.toStep(paymentSteps.PAYMENT_CLAIM_REFUND);
  }

  onSurcharge() {
    this.isPaymentUnderpaid = false;
    this.stepService.currentStep$$.next({ ...this.stepService.currentStep$$.value, isLocked: false });
  }

  resetPaymentCommonElements() {
    // hide previous step button
    this.stepService.currentStep$$.next({ ...this.stepService.currentStep$$.value, isLocked: true });
    // hide terminate payment button
    this.utilsService.hideTerminatePayment();
    // hide payment nav
    this.isPaymentNavShown = false;
    // hide payment currency alert
    this.isPaymentCurrencyAlertShown = false;
    // hide payment currency info
    this.isPaymentCurrencyInfoShown = false;
    // hide payment details info
    this.isPaymentDetailsInfoShown = false;
    // set default payment nav item
    this.selectedNavigationItem = paymentNavigationItems.SCAN;
    // remove selected payment option
    // this.paymentService.selectedPaymentOption$$.next(new PaymentOptionModel());
    // remove deep link button
    this.selectedDeepLink = '';
    // remove payment description
    this.paymentDescriptionType = '';
  }
}
